/**
 * Todos los textos de la interfaz viven aquí (§1.3): cambiar el nombre de la app o traducirla
 * no obliga a buscar cadenas por el código.
 */
export const t = {
  app: {
    name: 'Atril',
    tagline: 'Tu cancionero personal',
  },
  nav: {
    repertoire: 'Repertorio',
    settings: 'Ajustes',
    back: 'Volver',
  },
  sheet: {
    empty: 'Esta ficha todavía no tiene letra.',
  },
  storage: {
    opening: 'Abriendo tu repertorio…',
    errorTitle: 'No se puede abrir la base de datos local',
    errorText:
      'Atril guarda tus canciones en este dispositivo y el navegador no deja abrirlas. Puede pasar en una pestaña privada o si falta espacio. Cierra y vuelve a abrir la app; si sigue igual, copia los detalles y pásalos.',
    retry: 'Reintentar',
    copyDetails: 'Copiar detalles',
  },
  repertoire: {
    title: 'Repertorio',
    searchLabel: 'Buscar canciones',
    searchPlaceholder: 'Título o artista',
    add: 'Añadir canción',
    emptyTitle: 'Todavía no hay canciones',
    emptyText:
      'Pega la letra con los acordes de una canción y Atril la guarda en este dispositivo. Las letras no salen de aquí.',
    emptyExample: 'Probar con tres canciones de ejemplo',
    noResults: (query: string) => `Ninguna canción coincide con «${query}».`,
    sort: 'Orden',
    sortTitle: 'Título',
    sortArtist: 'Artista',
    sortRecent: 'Recientes',
    count: (n: number) => (n === 1 ? '1 canción' : `${n} canciones`),
    play: 'Tocar',
    edit: 'Editar',
    badgeKey: (key: string) => `Tono ${key}`,
    badgeCapo: (fret: number) => `Cejilla ${fret}`,
    badgeBpm: (bpm: number) => `${bpm} BPM`,
    review: 'Por revisar',
    status: { learning: 'Aprendiendo', polishing: 'Para pulir', mastered: 'Dominada' },
    backupDue:
      'Hace tiempo que no haces una copia de seguridad. Safari puede borrar los datos de una web sin avisar.',
    backupNow: 'Ir a copia de seguridad',
    backupLater: 'Más tarde',
    loadError: 'No se ha podido leer el repertorio.',
  },
  newSong: {
    title: 'Añadir canción',
    lead: 'Pega la transcripción con los acordes sobre la letra, o en ChordPro. Atril la entiende, la guarda en este dispositivo y la marca «por revisar» hasta que la aceptes.',
    songTitle: 'Título',
    songArtist: 'Artista',
    source: 'De dónde la has copiado (opcional)',
    sourcePlaceholder: 'Dirección de la página',
    text: 'Texto de la canción',
    textPlaceholder: 'Pega aquí la letra con los acordes encima…',
    openFile: 'Abrir un archivo…',
    fileHint: '.cho, .chordpro o .txt',
    formatChordpro: 'Detectado: ChordPro',
    formatSheet: 'Detectado: acordes sobre la letra',
    summary: (sections: number, lines: number, chords: number) =>
      `${sections} ${sections === 1 ? 'sección' : 'secciones'} · ${lines} líneas · ${chords} acordes`,
    notation: (notation: string) => `Notación ${notation}`,
    notationAmerican: 'americana (C, Dm, G7)',
    notationLatin: 'latina (Do, Rem, Sol7)',
    preview: 'Vista previa',
    issues: 'Avisos',
    save: 'Guardar canción',
    saving: 'Guardando…',
    duplicate: 'Ya tienes una canción con este título y este artista.',
    needTitle: 'Falta el título.',
    saveError: (message: string) => `No se ha podido guardar: ${message}`,
    privacy: 'La letra se queda solo en este dispositivo y en tus copias de seguridad.',
    fallbackTitle: (title: string) => `Se usará el título del archivo: «${title}».`,
  },
  debug: {
    toggle: 'Consola',
    title: 'Consola de depuración',
    clear: 'Vaciar',
    copy: 'Copiar',
    close: 'Cerrar',
    empty: 'Sin mensajes.',
    copied: 'Copiado',
  },
  pwa: {
    updateReady: 'Hay una versión nueva de Atril.',
    updateNow: 'Actualizar',
    later: 'Más tarde',
    offlineReady: 'Atril ya funciona sin conexión.',
  },
  clockSpike: {
    title: 'Spike del reloj',
    intro:
      'Pon el dispositivo en el atril con el volumen alto, pulsa «Empezar» y deja que suene. Fíjate en si el destello y el clic siguen juntos al final y en si se oye algún corte.',
    bpm: 'BPM',
    duration: 'Duración',
    minutes: (n: number) => `${n} min`,
    keepAwake: 'Mantener la pantalla encendida',
    playbackSession: 'Sonar aunque el dispositivo esté en silencio',
    start: 'Empezar',
    stop: 'Parar',
    running: 'Sonando…',
    noFrames: 'la pantalla no se está redibujando',
    metrics: 'Medidas',
    minute: 'Minuto',
    beats: 'Pulsos',
    meanLag: 'Retraso medio del destello',
    maxLag: 'Retraso máximo',
    skippedBeats: 'Destellos perdidos',
    longFrames: 'Fotogramas lentos',
    scheduled: 'Clics programados',
    missed: 'Clics saltados',
    minLead: 'Antelación mínima',
    maxTickGap: 'Mayor hueco del temporizador',
    drift: 'Reloj de audio frente al del sistema',
    frames: 'Fotogramas pintados',
    frameSources: (outputTimestamp: number, currentTime: number) =>
      `${outputTimestamp} con getOutputTimestamp, ${currentTime} con currentTime`,
    hidden: 'Pasos a segundo plano',
    questions: 'Tu impresión',
    together: '¿El destello y el clic seguían juntos al final?',
    glitches: '¿Has oído cortes o clics irregulares?',
    verdict: 'Resultado indicativo',
    verdictNote: 'El umbral definitivo se fija tras medir los tres dispositivos (§10, E0).',
    previous: 'Último resultado guardado',
    reasons: {
      missed: (n: number) => `Se saltaron ${n} clics: el temporizador se durmió.`,
      lag: (ms: number) => `El destello llegó a ir ${ms} ms tarde.`,
      skipped: (n: number) => `La pantalla no llegó a mostrar ${n} destellos.`,
      drift: (ms: number) =>
        `El retraso medio del destello creció ${ms} ms entre el primer y el último minuto.`,
      hidden: 'La app pasó a segundo plano: repite la prueba sin bloquear la pantalla.',
      notTogether: 'Has notado que el destello y el clic se separan.',
      glitches: 'Has oído cortes.',
      short: 'La prueba ha durado menos de 10 minutos.',
    },
    allGood: 'Sin problemas detectados.',
  },
  deviceSpike: {
    title: 'Spike de dispositivo',
    intro:
      'Prueba, una a una, las funciones que el iPad tiene que cumplir antes de construir el atril. Al final, copia los resultados y pégalos en el chat.',
    compat: {
      title: '1. Compatibilidad',
      text: 'React, Zod, el parser de ChordPro y la base de datos local arrancan en este navegador.',
      run: 'Comprobar',
      standalone: 'Abierta como app instalada',
      installHint:
        'En el iPad: Safari → Compartir → «Añadir a pantalla de inicio», y abre Atril desde el icono.',
    },
    audio: {
      title: '2. Audio y modo silencio',
      text: 'Pulsa «Sonar». Después activa el modo silencio (Centro de control → campana) y repite, con y sin el ajuste para ignorar el silencio.',
      play: 'Sonar',
      bypass: 'Sonar aunque el dispositivo esté en silencio',
      heard: '¿Se ha oído?',
      normal: 'Sin modo silencio',
      silent: 'En silencio, sin el ajuste',
      silentBypass: 'En silencio, con el ajuste',
    },
    awake: {
      title: '3. Pantalla encendida',
      text: 'Activa uno de los métodos y deja el dispositivo quieto 30 minutos sin tocarlo. Anota la batería al empezar y al terminar.',
      auto: 'Automático',
      video: 'Vídeo con pista silenciosa',
      wakeLock: 'Wake Lock',
      stop: 'Desactivar',
      active: (method: string, minutes: number) => `Activo (${method}) desde hace ${minutes} min.`,
      inactive: 'Inactivo.',
      batteryStart: 'Batería al empezar',
      batteryEnd: 'Batería al terminar',
      batteryUnset: '–',
      stayedOn: '¿La pantalla siguió encendida todo el tiempo?',
      notAvailable: 'No disponible en este navegador.',
      autoHint:
        'Automático: Wake Lock donde existe; en el iPad, vídeo con pista silenciosa. Puede pausar la música de otras apps.',
    },
    resume: {
      title: '4. Recuperación tras cierre',
      text: 'Pulsa «Guardar marca», abre varias apps pesadas o cierra Atril desde el multitarea, y vuelve a abrirla: debe volver aquí con la misma marca.',
      mark: 'Guardar marca',
      current: (value: number) => `Marca actual: ${value}`,
      boots: (n: number) => `Arranques registrados: ${n}`,
      restored: (value: number, ago: string) => `Recuperada la marca ${value}, guardada ${ago}.`,
      nothing: 'Nada que recuperar todavía.',
    },
    keys: {
      title: '5. Teclado o pedal',
      text: 'Pulsa teclas o el pedal Bluetooth. Aquí aparece lo que recibe la app.',
      empty: 'Sin pulsaciones.',
    },
    perf: {
      title: '6. Rendimiento del resaltado',
      text: 'Durante 15 segundos la letra avanza y se resalta una línea por pulso. Mide los fotogramas por segundo.',
      run: 'Medir',
      result: (fps: number, slow: number) => `${fps} fotogramas por segundo, ${slow} lentos.`,
    },
    results: {
      title: '7. Resultados',
      text: 'Copia los resultados y pégalos en el chat.',
    },
  },
  sections: {
    intro: 'Intro',
    verse: 'Estrofa',
    prechorus: 'Preestribillo',
    chorus: 'Estribillo',
    bridge: 'Puente',
    instrumental: 'Instrumental',
    solo: 'Solo',
    interlude: 'Interludio',
    outro: 'Final',
    tab: 'Tablatura',
    other: 'Parte',
  },
  play: {
    back: 'Repertorio',
    edit: 'Editar',
    now: 'Ahora',
    next: 'Después',
    noDiagram: 'Sin diagrama',
    voicingOf: (n: number, total: number) => `Digitación ${n} de ${total}`,
    voicingHint: 'Toca el diagrama para ver otra digitación',
    sections: 'Secciones',
    capo: (fret: number) => `Cejilla ${fret}`,
    key: (key: string) => `Tono ${key}`,
    keyEstimated: (key: string) => `Tono ${key} (estimado)`,
    bpm: (bpm: number) => `${bpm} BPM`,
    options: 'Ajustes de la hoja',
    smaller: 'A−',
    larger: 'A+',
    fontSize: 'Letra',
    notationAmerican: 'C D E',
    notationLatin: 'Do Re Mi',
    notation: 'Notación',
    theme: 'Tema',
    themeDark: 'Oscuro',
    themeLight: 'Claro',
    themeContrast: 'Contraste',
    awake: 'Pantalla encendida',
    awakeOn: 'Activa',
    awakeOff: 'Desactivada',
    awakeFailed:
      'No se pudo mantener la pantalla encendida: ajusta el bloqueo automático del dispositivo.',
    tuneTitle: 'Tono y cejilla',
    transpose: 'Transponer',
    semitones: (n: number) => (n === 0 ? 'Original' : n > 0 ? `+${n}` : `−${Math.abs(n)}`),
    capoLabel: 'Cejilla',
    capoNone: 'Sin cejilla',
    sounding: (key: string) => `Suena en ${key}`,
    reset: 'Volver al original',
    saveTune: 'Guardar como revisión',
    tuneSaved: 'Guardado como una revisión nueva.',
    tuneHint:
      'Transponer cambia el sonido y las formas. La cejilla cambia la forma de los dedos sin cambiar el sonido. Lo que ves aquí no se guarda hasta que lo pidas.',
    review: 'Esta ficha viene de una importación: está por revisar.',
    reviewAccept: 'La he revisado',
    notFound: 'Esta canción no existe.',
    loading: 'Cargando…',
    moveHint: 'Flechas, espacio o pedal para moverte; Intro para tocar o pausar la guía',
  },
  transport: {
    label: 'Guía',
    play: 'Tocar',
    resume: 'Seguir',
    pause: 'Pausa',
    stop: 'Parar',
    stopHint: 'Para y vuelve a donde empezaste',
    slower: 'Más lento',
    faster: 'Más rápido',
    bpm: (bpm: number) => `${bpm} BPM`,
    tap: 'Tap',
    tapHint: 'Toca al ritmo, al menos tres veces',
    /** «Más», con lo que está activo o apagado ahí dentro para que se vea sin abrirlo. */
    more: (loop: boolean, metronome: boolean) =>
      ['Más', loop ? 'bucle' : null, metronome ? null : 'sin clic'].filter(Boolean).join(' · '),
    metronomeOn: 'Clic sí',
    metronomeOff: 'Clic no',
    loopSection: 'Repetir sección',
    loopAB: 'Bucle A–B',
    loopPickEnd: 'Elige el final…',
    loop: (label: string) => `Bucle: ${label}`,
    clearLoop: 'Quitar el bucle',
    comfortable: (step: number) => `Me sale +${step}`,
    comfortableSaved: (bpm: number, next: number) =>
      `Anotado: te sale a ${bpm}. Ahora a ${next} BPM.`,
    original: (bpm: number) => `Original ${bpm}`,
    fragment: (label: string) => `Repetir ${label}`,
    timingEstimated: 'Tiempos estimados',
    timingReviewed: 'Tiempos revisados',
    adjustTiming: 'Tiempos',
    meterAssumed: (signature: string) => `${signature} supuesto`,
    position: 'Compás · pulso',
    ended: 'Fin de la canción.',
    countInNow: 'Entra en',
  },
  practice: {
    title: 'Práctica',
    status: 'Estado',
    statuses: { learning: 'Aprendiendo', polishing: 'Para pulir', mastered: 'Dominada' },
    notes: 'Notas de práctica',
    notesPlaceholder: 'Lo que quieras recordar la próxima vez…',
    comfortable: (bpm: number) => `Último tempo cómodo: ${bpm} BPM.`,
    noComfortable: 'Cuando algo te salga, pulsa «Me sale» y se anota el tempo.',
    countInTitle: 'Cuenta de entrada',
    countInBars: (bars: number) =>
      bars === 0 ? 'Sin cuenta' : bars === 1 ? '1 compás' : `${bars} compases`,
    stepTitle: '«Me sale» sube',
    step: (bpm: number) => `+${bpm} BPM`,
  },
  timing: {
    title: 'Tiempos',
    back: 'Volver a tocar',
    lead: 'Cuánto dura cada acorde, en pulsos. Parte de una estimación; grábalo tocando o escuchando la canción y corrige lo que haga falta. Nada se guarda hasta que pulses «Guardar».',
    status: {
      absent: 'Sin tiempos: se usa una estimación',
      estimated: 'Estimados: por revisar',
      reviewed: 'Revisados por ti',
    },
    meter: 'Compás',
    meterHint: (beats: number) => `${beats} pulsos por compás`,
    estimate: 'Estimar de nuevo',
    estimateHint: 'Sustituye todos los tiempos por una regla. Lo grabado se pierde.',
    rules: {
      'bar-per-chord': 'Un compás por acorde',
      'line-2-bars': 'Dos compases por línea',
      'line-4-bars': 'Cuatro compases por línea',
    },
    record: 'Grabar cambios',
    recordHint:
      'Pulsa en cada cambio de acorde, con el dedo, la barra espaciadora o el pedal. Con clic, entras tras la cuenta; sin clic (para seguir una grabación), el primer toque marca el primer acorde.',
    recordFromStart: 'Grabar desde el principio',
    recordHere: 'Grabar desde aquí',
    withClick: 'Con clic',
    withoutClick: 'Sin clic',
    tempo: 'Tempo de la toma',
    recording: 'Grabando',
    tapPad: 'Toca en cada cambio',
    waitingFirst: 'Toca cuando empiece el primer acorde',
    nowPlaying: 'Suena',
    nextChange: 'Siguiente cambio',
    undoMark: 'Deshacer la última marca',
    finish: 'Terminar',
    cancel: 'Cancelar',
    marks: (n: number) => (n === 1 ? '1 marca' : `${n} marcas`),
    grid: 'Ajustar a la rejilla',
    grids: { '1': 'Al pulso', '0.5': 'Al medio pulso', none: 'Sin ajustar' },
    gridHint: 'Lo que marcaste se conserva: puedes cambiar de rejilla cuando quieras.',
    sequence: 'Orden de la canción',
    sequenceHint:
      'Si la grabación repite una parte más veces de las que trae la hoja, añade la repetición aquí.',
    repeat: 'Repetir',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    remove: 'Quitar',
    beats: (n: number) => (n === 1 ? '1 pulso' : `${String(n).replace('.', ',')} pulsos`),
    shorter: 'Más corto',
    longer: 'Más largo',
    tapped: 'Marcado',
    barLabel: (bar: number, beat: number) => `${bar}·${beat}`,
    offBar: (beats: number) => {
      const amount = Math.abs(beats)
      const verb =
        beats > 0 ? (amount === 1 ? 'sobra' : 'sobran') : amount === 1 ? 'falta' : 'faltan'
      return `No cierra el compás: le ${verb} ${String(amount).replace('.', ',')} ${amount === 1 ? 'pulso' : 'pulsos'}`
    },
    total: (bars: string, minutes: string) => `${bars} compases · ${minutes} a este tempo`,
    preview: 'Escuchar',
    stopPreview: 'Parar',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado como una revisión nueva.',
    savedNothing: 'No había cambios que guardar.',
    unsaved: 'Cambios sin guardar',
    saveError: (message: string) => `No se ha podido guardar: ${message}`,
    noChords: 'Esta ficha no tiene acordes: no hay nada que medir.',
    reason: 'Tiempos',
  },
  calibration: {
    title: 'Retraso del sonido',
    text: 'Con altavoces o auriculares Bluetooth, el clic llega tarde y la guía se adelanta. Pulsa «Calibrar», escucha el clic y toca en la pantalla (o la barra espaciadora) a la vez que lo oyes, unas doce veces. Repítelo si cambias de altavoz.',
    current: (ms: number) => `Calibrado: ${ms} ms.`,
    automatic: 'Sin calibrar: se usa lo que dice el navegador.',
    start: 'Calibrar',
    tapHere: 'Toca a la vez que el clic',
    taps: (n: number) => `${n} toques`,
    result: (ms: number, spread: number) => `Retraso: ${ms} ms (±${spread} ms).`,
    unreliable: 'Los toques salieron muy dispersos o fueron pocos. Prueba otra vez.',
    save: 'Usar este valor',
    reset: 'Volver a lo automático',
    cancel: 'Cancelar',
    adjust: 'Ajuste fino',
  },
  editor: {
    title: 'Editar ficha',
    back: 'Volver a tocar',
    songTitle: 'Título',
    songArtist: 'Artista',
    shapeKey: 'Tono de las formas',
    keyNone: 'Sin tono',
    capo: 'Cejilla (tal como está escrita)',
    capoNone: 'Sin cejilla',
    tempo: 'Tempo de referencia',
    tempoNone: 'Sin tempo',
    tempoDown: 'Bajar tempo',
    tempoUp: 'Subir tempo',
    time: 'Compás',
    timeNone: 'Sin compás',
    notes: 'Notas',
    provenanceLabel: 'Procedencia',
    provenance: (kind: string, needsReview: boolean) =>
      `${
        (
          {
            source: 'De una fuente',
            ai: 'Estimado por la IA',
            user: 'Tuyo',
            import: 'Importado',
            rule: 'Estimado por una regla',
            sync: 'Sincronizado',
          } as Record<string, string>
        )[kind] ?? kind
      }${needsReview ? ' · por revisar' : ''}`,
    body: 'Letra y acordes',
    bodyHint:
      'Cada acorde va entre corchetes, justo antes de la sílaba donde cae: [Am]Cuento los [G]faroles. Una sección empieza con {start_of_verse} y acaba con {end_of_verse}.',
    insert: 'Insertar',
    insertChord: 'Acorde [ ]',
    insertVerse: 'Estrofa',
    insertChorus: 'Estribillo',
    insertBridge: 'Puente',
    insertIntro: 'Intro',
    insertRepeat: 'Repetir',
    preview: 'Vista previa',
    issues: 'Avisos',
    noIssues: 'Sin avisos.',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado.',
    savedNothing: 'No había cambios que guardar.',
    unsaved: 'Cambios sin guardar',
    allSaved: 'Todo guardado',
    reason: '¿Qué has cambiado? (opcional)',
    saveError: (message: string) => `No se ha podido guardar: ${message}`,
    draftRecovered: 'Se han recuperado cambios que no habías guardado.',
    draftDiscard: 'Descartar',
    review: 'Esta ficha está por revisar.',
    reviewAccept: 'La he revisado',
    history: 'Historial de revisiones',
    historyHint:
      'Cada vez que guardas se crea una revisión nueva; las anteriores no se tocan. Solo tú decides cuál es la principal.',
    main: 'Principal',
    makeMain: 'Hacer principal',
    author: { user: 'Tú', import: 'Importación', ai: 'IA', sync: 'Sincronización' },
    noReason: 'Sin motivo anotado',
    export: 'Exportar',
    exportChordpro: 'ChordPro (.cho)',
    exportSheet: 'Acordes sobre la letra (.txt)',
    exportLosses: (losses: string) => `No se guarda en ese formato: ${losses}.`,
    delete: 'Borrar canción',
    deleteAsk: 'Se borrará la canción con todo su historial. No se puede deshacer.',
    deleteYes: 'Sí, borrar',
    deleteNo: 'No',
    notFound: 'Esta canción no existe.',
    loading: 'Cargando…',
    tab: { edit: 'Editar', preview: 'Vista previa' },
  },
  settings: {
    title: 'Ajustes',
    sheetTitle: 'La hoja',
    notation: 'Notación de los acordes',
    notationHint: 'Cómo se muestran en pantalla, sea cual sea la notación de la ficha.',
    theme: 'Tema',
    fontSize: 'Tamaño de la letra',
    keepAwake: 'Mantener la pantalla encendida al tocar',
    pedalTitle: 'Pedal y teclado',
    pedalHint:
      'Un pedal Bluetooth llega a la app como un teclado. Pulsa «Añadir tecla» y después el pedal para que Atril lo aprenda.',
    actions: {
      next: 'Siguiente acorde',
      previous: 'Acorde anterior',
      nextSection: 'Sección siguiente',
      previousSection: 'Sección anterior',
      playPause: 'Tocar o pausar la guía',
    },
    addKey: 'Añadir tecla',
    listening: 'Pulsa una tecla o el pedal…',
    cancel: 'Cancelar',
    removeKey: (key: string) => `Quitar ${key}`,
    noKeys: 'Ninguna',
    resetPedal: 'Restablecer las teclas',
    backup: {
      title: 'Copia de seguridad',
      text: 'Safari puede borrar los datos de una web sin avisar. La copia es un archivo con todas tus canciones, tus revisiones y tus ajustes: guárdalo en un sitio seguro.',
      last: (ago: string) => `Última copia: ${ago}.`,
      never: 'Todavía no has hecho ninguna copia.',
      export: 'Guardar copia',
      exported: (songs: number) => `Copia guardada (${songs} canciones).`,
      restore: 'Restaurar una copia…',
      summary: (songs: number, arrangements: number, revisions: number, when: string) =>
        `La copia tiene ${songs} canciones, ${arrangements} arreglos y ${revisions} revisiones, y es del ${when}.`,
      replace: 'Reemplazar todo',
      merge: 'Añadir lo que falta',
      replaceWarn: 'Reemplazar borra lo que hay ahora en este dispositivo y deja solo la copia.',
      mergeHint: 'Añadir lo que falta deja intacto lo que ya tienes.',
      cancel: 'Cancelar',
      done: (added: number, skipped: number) =>
        skipped > 0
          ? `Restaurado: ${added} elementos añadidos, ${skipped} ya estaban.`
          : `Restaurado: ${added} elementos.`,
      error: (message: string) => `No se ha podido restaurar: ${message}`,
      exportError: (message: string) => `No se ha podido guardar la copia: ${message}`,
      count: (songs: number) => `Ahora mismo hay ${songs} canciones en este dispositivo.`,
    },
    storage: {
      title: 'Almacenamiento',
      persistent: 'Almacenamiento persistente',
      yes: 'Sí',
      no: 'No: Safari podría borrarlo si falta espacio',
      unknown: 'No disponible en este navegador',
    },
    diagnostics: {
      title: 'Diagnóstico',
      text: 'Las pruebas de la entrega 0, por si hay que volver a medir un dispositivo.',
      clock: 'Spike del reloj',
      device: 'Spike de dispositivo',
      console: 'La consola de depuración está en el botón «Consola», abajo a la derecha.',
    },
  },
  common: {
    copyResults: 'Copiar resultados',
    copied: 'Copiado',
    yes: 'Sí',
    no: 'No',
    ok: 'Vale',
    unknown: 'Desconocido',
  },
} as const

export function formatAgo(ms: number): string {
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `hace ${seconds} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `hace ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `hace ${hours} h`
  return `hace ${Math.round(hours / 24)} días`
}
